import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Input, FormGroup, Label, Alert, Form, Modal, ModalHeader, ModalBody, ModalFooter, Progress, Badge } from 'reactstrap';

import '../Admin.css';
import { getWorkspaceBranding, updateWorkspaceBranding, uploadBrandingAsset, verifyCustomDomain, getDomainInstructions, generateVerificationToken, requestSSLCertificate, verifySSLCertificate, checkSSLStatus } from '../../../store/Admin';

const Branding = ({ accessType, workspaceId }) => {
  const dispatch = useDispatch();
  const { currentWorkspace } = useSelector(state => state['Settings']);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [sslStatus, setSslStatus] = useState('pending');
  const [verifyingSSL, setVerifyingSSL] = useState(false);
  const [copiedItems, setCopiedItems] = useState(new Set());
  const [domainStatus, setDomainStatus] = useState('pending');
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [domainInstructions, setDomainInstructions] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ logo: 0, favicon: 0, emailLogo: 0 });
  const [brandingData, setBrandingData] = useState({ logo: '', favicon: '', agencyName: '', customDomain: '', primaryColor: '#120348', secondaryColor: '#FBFBFF', emailLogo: '', emailFooter: '' });

  // Copy to clipboard function
  const copyToClipboard = async (text, label, itemKey) => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage({ type: 'success', text: `${label} copied to clipboard!` });

      // Add visual feedback
      setCopiedItems(prev => new Set([...prev, itemKey]));
      setTimeout(() => {
        setCopiedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemKey);
          return newSet;
        });
      }, 1500);

      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setMessage({ type: 'success', text: `${label} copied to clipboard!` });

      // Add visual feedback
      setCopiedItems(prev => new Set([...prev, itemKey]));
      setTimeout(() => {
        setCopiedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemKey);
          return newSet;
        });
      }, 1500);

      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  useEffect(() => {
    if (currentWorkspace?.['_id'])
      dispatch(getWorkspaceBranding({ workspaceId: currentWorkspace['_id'] }));
  }, [currentWorkspace, dispatch]);

  useEffect(() => {
    if (currentWorkspace?.['branding']) {
      console.log('*********currentWorkspace = ', currentWorkspace['branding']);
      setBrandingData({ logo: currentWorkspace['branding']['logo'] || '', favicon: currentWorkspace['branding']['favicon'] || '', agencyName: currentWorkspace['branding']['agencyName'] || '', customDomain: currentWorkspace['branding']['customDomain'] || '', primaryColor: currentWorkspace['branding']['primaryColor'] || '#120348', secondaryColor: currentWorkspace['branding']['secondaryColor'] || '#FBFBFF', emailLogo: currentWorkspace['branding']['emailLogo'] || '', emailFooter: currentWorkspace['branding']['emailFooter'] || '' });
      setDomainStatus(currentWorkspace['branding']['domainStatus'] || 'pending');
      setSslStatus(currentWorkspace['branding']['sslStatus'] || 'pending');

      // Load domain instructions if custom domain exists
      if (currentWorkspace['branding']['customDomain'] && !domainInstructions)
        loadDomainInstructions();
    }
  }, [currentWorkspace]);

  const handleInputChange = (field, value) => {
    setBrandingData(prev => ({ ...prev, [field]: value }));
    if (message['text']) setMessage({ type: '', text: '' });

    // Load domain instructions when custom domain is entered
    if (field === 'customDomain' && value && !domainInstructions)
      loadDomainInstructions();
  };

  const handleFileUpload = async (field, file) => {
    if (!file) return;

    const validTypes = field === 'favicon' ? ['image/png', 'image/x-icon', 'image/vnd.microsoft.icon'] : ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];

    if (!validTypes.includes(file['type'])) {
      setMessage({ type: 'error', text: `Invalid file type for ${field}. Please use ${field === 'favicon' ? 'PNG or ICO' : 'PNG, JPG, or SVG'} format.` });
      return;
    }

    const maxSize = field === 'favicon' ? 1024 * 1024 : 5 * 1024 * 1024;
    if (file['size'] > maxSize) {
      setMessage({ type: 'error', text: `File size too large. Maximum ${field === 'favicon' ? '1MB' : '5MB'} allowed.` });
      return;
    }

    setUploadProgress(prev => ({ ...prev, [field]: 10 }));

    dispatch(uploadBrandingAsset({
      workspaceId: currentWorkspace['_id'],
      field: field,
      file: file,
      onProgress: (progressEvent) => {
        if (progressEvent['lengthComputable']) {
          const progress = Math.round((progressEvent['loaded'] * 100) / progressEvent['total']);
          setUploadProgress(prev => ({ ...prev, [field]: progress }));
        }
      },
      callback: (result, error) => {
        setUploadProgress(prev => ({ ...prev, [field]: 0 }));
        if (error) {
          console.error('Upload error:', error);
          setMessage({ type: 'error', text: error['response']?.['data']?.['message'] || `Failed to upload ${field}` });
        } else if (result) {
          setBrandingData(prev => ({ ...prev, [field]: result['url'] }));
          setMessage({ type: 'success', text: `${field.charAt(0).toUpperCase() + field.slice(1)} uploaded successfully!` });
        }
      }
    }))
  }

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    dispatch(updateWorkspaceBranding({
      workspaceId: currentWorkspace['_id'],
      branding: brandingData,
      callback: (result) => {
        setSaving(false);
        if (result) {
          loadDomainInstructions();
          setMessage({ type: 'success', text: 'Agency branding settings saved successfully!' });
        } else setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
      }
    }))
  }

  const handleReset = () => {
    setDomainStatus('pending');
    setDomainInstructions(null);
    setMessage({ type: '', text: '' });
    setBrandingData({ logo: '', favicon: '', agencyName: '', customDomain: '', primaryColor: '#120348', secondaryColor: '#FBFBFF', emailLogo: '', emailFooter: '' });
  }

  const loadDomainInstructions = () => {
    dispatch(getWorkspaceBranding({ workspaceId: currentWorkspace['_id'] }));
    dispatch(getDomainInstructions({
      workspaceId: currentWorkspace['_id'],
      callback: (result) => {
        if (result) {
          setDomainInstructions(result);
          // Update domain and SSL status from backend response
          if (result.domainStatus) setDomainStatus(result.domainStatus);
          if (result.sslStatus) setSslStatus(result.sslStatus);
        }
      }
    }))
  }

  const verifyDomain = () => {
    if (!brandingData['customDomain']) {
      setMessage({ type: 'error', text: 'Please enter a custom domain first' });
      return;
    }

    setVerifyingDomain(true);
    setMessage({ type: '', text: '' });

    dispatch(verifyCustomDomain({
      workspaceId: currentWorkspace['_id'],
      callback: (result, error) => {
        setVerifyingDomain(false);
        if (error) {
          setDomainStatus('failed');
          setMessage({ type: 'error', text: error['response']?.['data']?.['message'] || 'Domain verification failed' });
        } else {
          setDomainStatus('verified');
          if (result?.sslRequested) setSslStatus('provisioning');
          loadDomainInstructions();
          setMessage({ type: 'success', text: 'Domain verified successfully! Please add the SSL validation DNS records below.' });
        }
      }
    }))
  }

  const verifySSL = () => {
    setVerifyingSSL(true);
    setMessage({ type: '', text: '' });
    dispatch(verifySSLCertificate({
      workspaceId: currentWorkspace['_id'],
      callback: (result, error) => {
        setVerifyingSSL(false);
        if (error) {
          setMessage({ type: 'error', text: error['response']?.['data']?.['message'] || 'SSL verification failed' });
        } else {
          if (result?.['validated']) {
            setSslStatus('active');
            setMessage({ type: 'success', text: 'SSL certificate validated and activated successfully!' });
            // Reload domain instructions to reflect new status
            loadDomainInstructions();
            // Update workspace data
            dispatch(getWorkspaceBranding({ workspaceId: currentWorkspace['_id'] }));
          } else {
            setMessage({ type: 'warning', text: result?.['message'] || 'SSL certificate not yet validated. Please add the DNS record and wait.' });
          }
        }
      }
    }))
  }

  const retrySSL = () => {
    setVerifyingSSL(true);
    setMessage({ type: '', text: '' });

    dispatch(requestSSLCertificate({
      workspaceId: currentWorkspace['_id'],
      callback: (result, error) => {
        setVerifyingSSL(false);
        if (error) {
          setSslStatus('failed');
          setMessage({
            type: 'error',
            text: error['response']?.['data']?.['message'] || 'Failed to request SSL certificate'
          });
        } else {
          setSslStatus('provisioning');
          setMessage({
            type: 'success',
            text: 'SSL certificate requested successfully! Please add the DNS records below.'
          });
          // Reload domain instructions to show SSL validation records
          loadDomainInstructions();
          // Update workspace data
          dispatch(getWorkspaceBranding({ workspaceId: currentWorkspace['_id'] }));
        }
      }
    }));
  }

  const getDomainStatusColor = () => {
    switch (domainStatus) {
      case 'verified': return 'success';
      case 'failed': return 'danger';
      case 'verifying': return 'warning';
      default: return 'secondary';
    }
  }

  const getDomainStatusText = () => {
    switch (domainStatus) {
      case 'verified': return 'Verified';
      case 'failed': return 'Failed';
      case 'verifying': return 'Verifying...';
      default: return 'Pending';
    }
  }

  const getSslStatusColor = () => {
    switch (sslStatus) {
      case 'active': return 'success';
      case 'failed': return 'danger';
      case 'expired': return 'danger';
      case 'provisioning': return 'warning';
      default: return 'secondary';
    }
  }

  const getSslStatusText = () => {
    switch (sslStatus) {
      case 'active': return 'Active';
      case 'failed': return 'Failed';
      case 'expired': return 'Expired';
      case 'provisioning': return 'Provisioning';
      default: return 'Pending';
    }
  }

  if (accessType !== 'agency') return (
    <Container fluid className="mt-4">
      <Alert color="warning">Branding settings are only available for Agency plan subscribers.</Alert>
    </Container>
  )

  return (

    <div className='content-dashboard'>
      <div className='title-area-brancding d-flex justify-content-between align-items-center mb-4'>
        <div className='left-area'>
          <h3>Branding</h3>
          <p className="">
            Customize your agency's branding and white-label settings
          </p>
        </div>
        <div className='right-area'>
          <button onClick={handleReset} className="btn-style-border mr-3">
            <i className="fa fa-undo mr-1"></i>
            Reset
          </button>
          <button className="btn-style-new" onClick={handleSave} disabled={saving}>
            {saving && <i className="fa fa-spinner fa-spin mr-1"></i>}
            <i className="fa fa-save mr-1"></i>
            Save Changes
          </button>
        </div>
      </div>
      {message['text'] &&
        <Row className="mb-4">
          <Col>
            <Alert color={message['type'] === 'success' ? 'success' : 'danger'}>
              {message['text']}
            </Alert>
          </Col>
        </Row>
      }

      <Row className="banadings-tab">
        <Col lg={8}>
          <Card>
            <CardHeader>
              <h5 className="mb-0">
                <i className=" mr-2">
                  <svg fill="currentColor" height="20px" width="20px" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 325.04 325.04">
                    <g>
                      <path d="M84.821,210.938c-4.646-3.671-10.469-5.693-16.396-5.693c-8.055,0-15.556,3.579-20.58,9.821
                            c-4.393,5.464-6.382,12.3-5.598,19.247c0.785,6.962,4.258,13.198,9.778,17.561c4.646,3.672,10.467,5.694,16.393,5.694
                            c8.061,0,15.563-3.586,20.583-9.834c4.389-5.455,6.376-12.283,5.595-19.227C93.813,221.545,90.342,215.305,84.821,210.938z
                            M79.648,240.215c-2.732,3.402-6.826,5.354-11.23,5.354c-3.283,0-6.379-1.075-8.951-3.108c-2.991-2.364-4.871-5.735-5.296-9.493
                            c-0.422-3.743,0.652-7.431,3.023-10.379c2.733-3.396,6.827-5.344,11.231-5.344c3.286,0,6.383,1.075,8.954,3.107
                            c2.99,2.364,4.869,5.737,5.292,9.497C83.092,233.589,82.02,237.269,79.648,240.215z"/>
                      <path d="M190.33,149.484c3.958,0,7.785-0.867,11.384-2.582c6.362-3.044,11.141-8.369,13.454-14.995
                              c2.301-6.584,1.887-13.669-1.163-19.944c-4.363-8.994-13.681-14.806-23.738-14.806c-3.966,0-7.801,0.87-11.399,2.587
                              c-6.36,3.034-11.141,8.355-13.458,14.983c-2.305,6.589-1.892,13.675,1.163,19.953C170.95,143.673,180.275,149.484,190.33,149.484z
                              M176.736,118.688c1.254-3.585,3.846-6.467,7.298-8.114c1.972-0.94,4.068-1.417,6.232-1.417c5.491,0,10.571,3.157,12.943,8.048
                              c1.645,3.384,1.868,7.199,0.63,10.745c-1.253,3.587-3.848,6.473-7.296,8.123c-1.965,0.937-4.056,1.411-6.214,1.411
                              c-5.496,0-10.586-3.162-12.968-8.055C175.718,126.05,175.496,122.236,176.736,118.688z"/>
                      <path d="M125.035,107.081c1.93,0.436,3.905,0.657,5.87,0.657c12.352,0,22.905-8.354,25.665-20.327
                              c1.551-6.789,0.358-13.778-3.356-19.682c-3.745-5.95-9.598-10.086-16.476-11.644c-1.938-0.44-3.921-0.664-5.895-0.664
                              c-12.344,0-22.886,8.363-25.637,20.334c-1.564,6.784-0.379,13.774,3.336,19.682C112.29,101.395,118.149,105.531,125.035,107.081z
                              M116.901,78.447c1.491-6.491,7.225-11.025,13.942-11.025c1.079,0,2.168,0.123,3.239,0.366c3.752,0.85,6.939,3.099,8.975,6.333
                              c2.006,3.187,2.65,6.957,1.816,10.605c-1.471,6.381-7.346,11.012-13.969,11.012c-1.076,0-2.161-0.122-3.229-0.363
                              c-3.753-0.845-6.94-3.092-8.975-6.326C116.698,85.864,116.059,82.1,116.901,78.447z"/>
                      <path d="M77.356,122.05c5.514-4.369,8.981-10.61,9.765-17.573c0.781-6.942-1.202-13.77-5.593-19.235
                              c-5.032-6.239-12.539-9.817-20.594-9.817c-5.921,0-11.738,2.016-16.385,5.681c-5.515,4.356-8.984,10.587-9.771,17.545
                              c-0.786,6.951,1.202,13.791,5.601,19.265c5.028,6.246,12.536,9.829,20.6,9.829C66.905,127.744,72.722,125.722,77.356,122.05z
                              M49.731,110.393c-2.376-2.956-3.451-6.648-3.027-10.396c0.424-3.753,2.302-7.119,5.282-9.474c2.571-2.028,5.666-3.1,8.949-3.1
                              c4.408,0,8.511,1.95,11.246,5.341c2.365,2.946,3.437,6.629,3.016,10.37c-0.423,3.764-2.303,7.142-5.292,9.51
                              c-2.559,2.027-5.646,3.099-8.925,3.099C56.567,115.744,52.467,113.792,49.731,110.393z"/>
                      <path d="M322.745,63.336c-1.037-1.046-2.887-2.293-5.806-2.293c-3.423,0-12.516,0-67.74,46.992c-1.11,0.944-2.23,1.901-3.354,2.865
                              c-9.867-25.739-27.203-48.686-49.542-65.284c-25.614-19.031-56.114-29.096-88.2-29.104c-0.01,0-0.017,0-0.025,0
                              c-21.654,0-47.976,7.566-68.697,19.749C13.981,51.193-0.005,71.163,0,92.49c0.008,25.748,14.53,36.518,26.199,45.171
                              c9.515,7.057,17.03,12.63,17.034,24.844c0.003,12.213-7.508,17.781-17.018,24.831c-11.665,8.648-26.184,19.412-26.176,45.163
                              c0.006,21.324,14.001,41.299,39.406,56.244c20.736,12.198,47.072,19.78,68.73,19.786c0.015,0,0.028,0,0.042,0
                              c39.305,0,76.254-15.171,104.044-42.72c27.436-27.197,42.695-63.246,43.096-101.661c9.316-10.601,18.341-21.138,26.58-31.067
                              c14.096-16.986,24.935-31.002,32.216-41.657C323.799,77.311,328.023,68.655,322.745,63.336z M203.814,257.287
                              c-25.529,25.308-59.475,39.242-95.596,39.242c-0.011,0-0.027,0-0.038,0c-38.707-0.011-96.13-26.903-96.141-64.034
                              c-0.006-19.707,10.354-27.388,21.323-35.52c10.253-7.602,21.874-16.218,21.87-34.474c-0.006-18.253-11.63-26.874-21.886-34.479
                              C22.372,119.883,12.006,112.196,12,92.487c-0.005-22.801,20.963-38.533,33.463-45.882c18.698-10.993,43.273-18.094,62.615-18.094
                              c0.007,0,0.015,0,0.021,0c29.491,0.008,57.517,9.254,81.048,26.736c21.702,16.125,38.268,38.761,46.994,64.049
                              c-26.025,22.724-54.207,48.924-75.195,69.98c-34.859,4.512-39.608,27.744-43.08,44.811c-2.956,14.532-4.875,21.558-16.092,22.458
                              c-2.764,0.222-5.015,2.308-5.446,5.047c-0.432,2.738,1.069,5.416,3.631,6.477c0.721,0.298,17.877,7.308,37.921,7.309
                              c0.003,0,0.005,0,0.007,0c13.968,0,25.95-3.386,35.612-10.063c11.906-8.228,19.979-21.273,24.036-38.767
                              c13.713-13.874,29.382-30.604,44.876-47.837C238.845,208.381,225.456,235.833,203.814,257.287z M185.48,225.412
                              c-6.358,25.196-22.356,37.968-47.594,37.967c0,0-0.004,0-0.006,0c-6.655,0-13.028-0.908-18.386-2.04
                              c6.4-6.527,8.399-16.349,10.13-24.858c3.297-16.208,6.415-31.547,31.923-35.191L185.48,225.412z M268.336,130.652
                              c-23.785,28.337-52.575,60.159-76.275,84.354l-3.669-3.698l-16.189-16.317c21.488-21.39,49.054-46.895,74.773-69.216l0,0
                              c2.998-2.602,5.977-5.171,8.913-7.675c29.847-25.455,45.489-36.533,53.468-41.354C304.592,84.773,293.616,100.534,268.336,130.652z
                              "/>
                    </g>
                  </svg>
                  </i>
                Brand Identity
              </h5>
            </CardHeader>
            <CardBody>
              <Form>
                <FormGroup>
                  <Label for="agencyName">Agency Name</Label>
                  <Input
                    type="text"
                    id="agencyName"
                    value={brandingData.agencyName}
                    onChange={(e) => handleInputChange('agencyName', e.target.value)}
                    placeholder="Enter your agency name"
                  />
                  <small className="form-text text-muted">
                    This name will appear in your custom branding throughout the platform
                  </small>
                </FormGroup>

                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="primaryColor">Primary Color</Label>
                      <div className="d-flex align-items-center">
                        <Input
                          type="color"
                          id="primaryColor"
                          value={brandingData.primaryColor}
                          onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                          style={{ width: '60px', height: '40px', padding: '5px' }}
                        />
                        <Input
                          type="text"
                          value={brandingData.primaryColor}
                          onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                          className="ml-2"
                          placeholder="#120348"
                        />
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="secondaryColor">Secondary Color</Label>
                      <div className="d-flex align-items-center">
                        <Input
                          type="color"
                          id="secondaryColor"
                          value={brandingData.secondaryColor}
                          onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                          style={{ width: '60px', height: '40px', padding: '5px' }}
                        />
                        <Input
                          type="text"
                          value={brandingData.secondaryColor}
                          onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                          className="ml-2"
                          placeholder="#FBFBFF"
                        />
                      </div>
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup>
                  <Label for="customDomain">Custom Domain</Label>
                  <Input
                    type="text"
                    id="customDomain"
                    value={brandingData.customDomain}
                    onChange={(e) => handleInputChange('customDomain', e.target.value)}
                    placeholder="app.youragency.com"
                  />
                  <small className="form-text text-muted">
                    Enter your root domain. Both app.youragency.com and www.app.youragency.com will be configured
                  </small>
                </FormGroup>
              </Form>
            </CardBody>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <h5 className="mb-0">
                <i className="fa fa-image mr-2"></i>
                Logo & Assets
              </h5>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Agency Logo</Label>
                    {brandingData['logo'] &&
                      <div className="mb-3">
                        <img src={brandingData['logo']} alt="Agency Logo" style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain' }} />
                      </div>
                    }
                    <div className="mb-2">
                      <button className="btn-style-new" size="sm" onClick={() => document.getElementById('logo-upload').click()}>
                        <i className="fa fa-upload mr-1"></i> Choose Logo
                      </button>
                      <Input id="logo-upload" type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml" onChange={(e) => handleFileUpload('logo', e['target']['files'][0])} style={{ display: 'none' }} />
                      {uploadProgress['logo'] > 0 && <Progress value={uploadProgress['logo']} className="mt-2" />}
                    </div>
                    <small className="form-text text-muted">Recommended: PNG, JPG, or SVG format. Max 5MB.</small>
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup>
                    <Label>Favicon</Label>
                    {brandingData['favicon'] &&
                      <div className="mb-3">
                        <img src={brandingData['favicon']} alt="Favicon" style={{ width: '32px', height: '32px' }} />
                      </div>
                    }
                    <div className="mb-2">
                      <button className="btn-style-new" color="primary" size="sm" onClick={() => document.getElementById('favicon-upload').click()}>
                        <i className="fa fa-upload mr-1"></i> Choose Favicon
                      </button>
                      <Input id="favicon-upload" type="file" accept="image/png,image/x-icon,image/vnd.microsoft.icon" onChange={(e) => handleFileUpload('favicon', e['target']['files'][0])} style={{ display: 'none' }} />
                      {uploadProgress['favicon'] > 0 && <Progress value={uploadProgress['favicon']} className="mt-2" />}
                    </div>
                    <small className="form-text text-muted">Recommended: 32x32 or 16x16 PNG/ICO format. Max 1MB.</small>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <h5 className="mb-0">
                <i className="fa fa-envelope mr-2"></i>
                Email Branding
              </h5>
            </CardHeader>
            <CardBody>
              <FormGroup>
                <Label>Email Logo</Label>
                {brandingData['emailLogo'] &&
                  <div className="mb-3">
                    <img src={brandingData['emailLogo']} alt="Email Logo" style={{ maxWidth: '300px', maxHeight: '80px', objectFit: 'contain' }} />
                  </div>
                }
                <div className="mb-2">
                  <button className="btn-style-new" size="sm" onClick={() => document.getElementById('email-logo-upload').click()}>
                    <i className="fa fa-upload mr-1"></i> Choose Email Logo
                  </button>
                  <Input id="email-logo-upload" type="file" accept="image/png,image/jpeg,image/jpg" onChange={(e) => handleFileUpload('emailLogo', e['target']['files'][0])} style={{ display: 'none' }} />
                  {uploadProgress['emailLogo'] > 0 && <Progress value={uploadProgress['emailLogo']} className="mt-2" />}
                </div>
                <small className="form-text text-muted">Logo to be used in email templates. Recommended: PNG or JPG format.</small>
              </FormGroup>

              <FormGroup>
                <Label for="emailFooter">Email Footer Text</Label>
                <Input
                  type="textarea"
                  id="emailFooter"
                  rows="3"
                  value={brandingData.emailFooter}
                  onChange={(e) => handleInputChange('emailFooter', e.target.value)}
                  placeholder="© 2024 Your Agency Name. All rights reserved."
                />
                <small className="form-text text-muted">
                  Custom footer text to appear in all outgoing emails
                </small>
              </FormGroup>
            </CardBody>
          </Card>
        </Col>

        <Col lg={4}>
          <Card>
            <CardHeader>
              <h6 className="mb-0">Preview</h6>
            </CardHeader>
            <CardBody>
              <div
                className="p-3 rounded"
                style={{
                  backgroundColor: brandingData.primaryColor + '10',
                  border: `2px solid ${brandingData.primaryColor}`
                }}
              >
                {brandingData.logo ? (
                  <img
                    src={brandingData.logo}
                    alt="Logo Preview"
                    style={{ maxWidth: '100%', maxHeight: '60px', objectFit: 'contain' }}
                  />
                ) : (
                  <div className="text-muted">Logo Preview</div>
                )}
                <h6 className="mt-2 mb-0" style={{ color: brandingData.primaryColor }}>
                  {brandingData.agencyName || 'Your Agency Name'}
                </h6>
                {brandingData.customDomain && (
                  <small className="text-muted">{brandingData.customDomain}</small>
                )}
              </div>

              <div className="mt-3">
                <h6>Color Scheme</h6>
                <div className="d-flex">
                  <div
                    className="mr-2"
                    title="Primary Color"
                    style={{ width: '40px', height: '40px', backgroundColor: brandingData.primaryColor, borderRadius: '4px' }}
                  ></div>
                  <div
                    title="Secondary Color"
                    style={{ width: '40px', height: '40px', backgroundColor: brandingData.secondaryColor, borderRadius: '4px' }}
                  ></div>
                </div>
              </div>
            </CardBody>
          </Card>

          {brandingData.customDomain && (
            <Card className="mt-3">
              <CardHeader>
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Domain & SSL Status</h6>
                  <div>
                    <Badge color={getDomainStatusColor()} className="mr-1">
                      <i className="fa fa-globe mr-1"></i>
                      {getDomainStatusText()}
                    </Badge>
                    {domainStatus === 'verified' && (
                      <Badge color={getSslStatusColor()}>
                        <i className="fa fa-lock mr-1"></i>
                        SSL: {getSslStatusText()}
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <div className="mb-3">
                  <strong>Domain:</strong>{' '}
                  {domainStatus === 'verified' && sslStatus === 'active' ? (
                    <a href={`https://${brandingData.customDomain}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                      {brandingData.customDomain} <i className="fa fa-external-link-alt ml-1"></i>
                    </a>
                  ) : <span>{brandingData.customDomain}</span>}
                  {domainStatus === 'verified' && (
                    <div className="small text-muted mt-1">
                      <strong>Redirects to:</strong> app.sellagent.ai
                    </div>
                  )}
                </div>

                {domainStatus !== 'verified' && (
                  <Button color="primary" size="sm" onClick={verifyDomain} disabled={verifyingDomain}>
                    {verifyingDomain && <i className="fa fa-spinner fa-spin mr-1"></i>}
                    <i className="fa fa-check mr-1"></i>
                    Verify Domain
                  </Button>
                )}

                {domainStatus === 'verified' && sslStatus === 'provisioning' && (
                  <Button color="primary" size="sm" onClick={verifySSL} disabled={verifyingSSL}>
                    {verifyingSSL && <i className="fa fa-spinner fa-spin mr-1"></i>}
                    <i className="fa fa-lock mr-1"></i>
                    Verify SSL Certificate
                  </Button>
                )}

                {domainStatus === 'verified' && sslStatus === 'failed' && (
                  <Button color="warning" size="sm" onClick={retrySSL} disabled={verifyingSSL}>
                    {verifyingSSL && <i className="fa fa-spinner fa-spin mr-1"></i>}
                    <i className="fa fa-exclamation-triangle mr-1"></i>
                    Retry SSL Setup
                  </Button>
                )}

                {domainStatus === 'verified' && sslStatus === 'failed' && (
                  <Alert color="danger" className="mt-3">
                    <i className="fa fa-exclamation-circle mr-2"></i>
                    <strong>SSL Setup Failed</strong>
                    <p className="mb-1 mt-2">
                      The automatic SSL certificate setup failed. This could be due to:
                    </p>
                    <ul className="mb-2">
                      <li>AWS configuration issues</li>
                      <li>Certificate request limits</li>
                      <li>Network connectivity problems</li>
                    </ul>
                    <p className="mb-0">
                      Click the "Retry SSL Setup" button above to try again.
                    </p>
                  </Alert>
                )}

                {domainInstructions && (
                  <div className="mt-3">
                    {/* Show status badges at the top */}
                    {domainInstructions['status'] === 'ssl-pending' && (
                      <div className="mb-3">
                        <Badge color="success" className="mr-2">
                          <i className="fa fa-check-circle mr-1"></i>
                          Domain: Verified
                        </Badge>
                        <Badge color="warning">
                          <i className="fa fa-lock mr-1"></i>
                          SSL: Pending
                        </Badge>
                      </div>
                    )}

                    <h6>{domainInstructions['message'] || 'DNS Configuration Required:'}</h6>

                    {/* Show domain redirect records for unverified domains */}
                    {domainInstructions['redirectRecords'] && domainInstructions['redirectRecords'].map((record, index) => (
                      <Alert key={index} color="info" className={index > 0 ? "mt-2" : ""}>
                        <small>
                          <strong>{index + 1}. {record['description']}:</strong><br />

                          <div className="mb-2">
                            <strong>Type:</strong> {record['type']}
                          </div>

                          <div className="mb-2">
                            <strong>Name:</strong>
                            <span
                              className="ml-1"
                              style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', padding: '2px 4px', borderRadius: '3px' }}
                              onClick={() => copyToClipboard(record['name'], 'Record name', `domain-${index}-name`)}
                              title="Click to copy record name"
                            >
                              {record['name']}
                              {copiedItems.has(`domain-${index}-name`) ? (
                                <i className="fa fa-check ml-1 text-success"></i>
                              ) : (
                                <i className="fa fa-copy ml-1 text-muted"></i>
                              )}
                            </span>
                          </div>

                          <div className="mb-2">
                            <strong>Value:</strong>
                            <span
                              className="ml-1"
                              style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', padding: '2px 4px', borderRadius: '3px' }}
                              onClick={() => copyToClipboard(record['value'], 'Record value', `domain-${index}-value`)}
                              title="Click to copy record value"
                            >
                              {record['value']}
                              {copiedItems.has(`domain-${index}-value`) ? (
                                <i className="fa fa-check ml-1 text-success"></i>
                              ) : (
                                <i className="fa fa-copy ml-1 text-muted"></i>
                              )}
                            </span>
                          </div>

                          {record['note'] && (
                            <div><em className="text-warning">{record['note']}</em></div>
                          )}
                        </small>
                      </Alert>
                    ))}

                    {/* Legacy support for single cnameOption */}
                    {!domainInstructions['redirectRecords'] && domainInstructions['cnameOption'] && (
                      <Alert color="info">
                        <small>
                          <strong>1. Domain Redirect Record:</strong><br />
                          Type: {domainInstructions['cnameOption']['type']}<br />
                          Name: {domainInstructions['cnameOption']['name']}<br />
                          Value: {domainInstructions['cnameOption']['value']}<br />
                        </small>
                      </Alert>
                    )}

                    {/* Show multiple SSL validation CNAME records */}
                    {domainInstructions['acmValidationRecords'] && domainInstructions['acmValidationRecords'].map((record, index) => (
                      <Alert key={index} color="warning" className={index > 0 ? "mt-2" : ""}>
                        <small>
                          <strong>{index + 1}. SSL Validation CNAME Record:</strong><br />
                          <em className="text-muted">{record['description']}:</em><br /><br />

                          <div className="mb-2">
                            <strong>Type:</strong> {record['type']}
                          </div>

                          <div className="mb-2">
                            <strong>Name:</strong>
                            <span
                              className="ml-1 text-break"
                              style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', padding: '2px 4px', borderRadius: '3px' }}
                              onClick={() => copyToClipboard(record['name'], 'Record name', `ssl-${index}-name`)}
                              title="Click to copy record name"
                            >
                              {record['name']}
                              {copiedItems.has(`ssl-${index}-name`) ? (
                                <i className="fa fa-check ml-1 text-success"></i>
                              ) : (
                                <i className="fa fa-copy ml-1 text-muted"></i>
                              )}
                            </span>
                          </div>

                          <div className="mb-1">
                            <strong>Value:</strong>
                            <span
                              className="ml-1 text-break"
                              style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', padding: '2px 4px', borderRadius: '3px' }}
                              onClick={() => copyToClipboard(record['value'], 'Record value', `ssl-${index}-value`)}
                              title="Click to copy record value"
                            >
                              {record['value']}
                              {copiedItems.has(`ssl-${index}-value`) ? (
                                <i className="fa fa-check ml-1 text-success"></i>
                              ) : (
                                <i className="fa fa-copy ml-1 text-muted"></i>
                              )}
                            </span>
                          </div>
                        </small>
                      </Alert>
                    ))}

                    {/* Backward compatibility: Show single SSL validation CNAME for verified domains with pending SSL */}
                    {!domainInstructions['acmValidationRecords'] && domainInstructions['acmValidation'] && (
                      <>
                        <Alert color="warning">
                          <small>
                            <strong>2. SSL Validation CNAME Record:</strong><br />
                            <em className="text-muted">Add this record to activate SSL certificate:</em><br /><br />

                            <div className="mb-2">
                              <strong>Type:</strong> {domainInstructions['acmValidation']['type'] || domainInstructions['acmValidation']['Type'] || 'CNAME'}
                            </div>

                            <div className="mb-2">
                              <strong>Name:</strong>
                              <span
                                className="ml-1 text-break"
                                style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', padding: '2px 4px', borderRadius: '3px' }}
                                onClick={() => copyToClipboard(domainInstructions['acmValidation']['name'] || domainInstructions['acmValidation']['Name'], 'Record name', 'legacy-ssl-name')}
                                title="Click to copy record name"
                              >
                                {domainInstructions['acmValidation']['name'] || domainInstructions['acmValidation']['Name']}
                                {copiedItems.has('legacy-ssl-name') ? (
                                  <i className="fa fa-check ml-1 text-success"></i>
                                ) : (
                                  <i className="fa fa-copy ml-1 text-muted"></i>
                                )}
                              </span>
                            </div>

                            <div className="mb-1">
                              <strong>Value:</strong>
                              <span
                                className="ml-1 text-break"
                                style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', padding: '2px 4px', borderRadius: '3px' }}
                                onClick={() => copyToClipboard(domainInstructions['acmValidation']['value'] || domainInstructions['acmValidation']['Value'], 'Record value', 'legacy-ssl-value')}
                                title="Click to copy record value"
                              >
                                {domainInstructions['acmValidation']['value'] || domainInstructions['acmValidation']['Value']}
                                {copiedItems.has('legacy-ssl-value') ? (
                                  <i className="fa fa-check ml-1 text-success"></i>
                                ) : (
                                  <i className="fa fa-copy ml-1 text-muted"></i>
                                )}
                              </span>
                            </div>
                          </small>
                        </Alert>
                      </>
                    )}

                    {/* Show success message for fully configured domains */}
                    {domainInstructions['status'] === 'complete' && (
                      <Alert color="success">
                        <i className="fa fa-check-circle mr-2"></i>
                        {domainInstructions['message']}
                        <div className="mt-2">
                          <Badge color="success" className="mr-2">
                            <i className="fa fa-globe mr-1"></i>
                            Domain: Verified
                          </Badge>
                          <Badge color="success">
                            <i className="fa fa-lock mr-1"></i>
                            SSL: Active
                          </Badge>
                        </div>
                      </Alert>
                    )}
                    {domainInstructions['steps'] && (
                      <div className="mt-2">
                        <h6>Setup Steps:</h6>
                        <div className="small">
                          {domainInstructions['steps'].map((step, idx) => (
                            <div key={idx}>{step}</div>
                          ))}
                        </div>
                      </div>
                    )}
                    {domainInstructions['notes'] && (
                      <Alert color="warning" className="mt-2">
                        <small>
                          <strong>Notes:</strong>
                          <ul className="mb-0 pl-3">
                            {domainInstructions['notes'].map((note, idx) => (
                              <li key={idx}>{note}</li>
                            ))}
                          </ul>
                        </small>
                      </Alert>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          <Card className="mt-3">
            <CardHeader>
              <h6 className="mb-0">White-Label Features</h6>
            </CardHeader>
            <CardBody>
              <Alert color="info">
                <h6>What's Included:</h6>
                <ul className="mb-0">
                  <li>Custom logo in navigation</li>
                  <li>Branded favicon</li>
                  <li>Custom color scheme</li>
                  <li>Email template branding</li>
                  <li>Custom domain support</li>
                  <li>Remove SellAgent branding</li>
                </ul>
              </Alert>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Branding;